/**
 * Chatbot base-URL resolution, with a dev-only runtime override.
 *
 * `VITE_CHATBOT_URL` is inlined at build time, so a Cloudflare Pages preview is
 * permanently wired to whichever chatbot the build was configured with
 * (chat-dev.franklin.ee). To exercise an app PR against a sandbox chatbot — one
 * running a chatbot PR's code, bound to that environment's Supabase branch — we
 * allow a localStorage override, gated behind the SAME flag as the Supabase
 * branch switcher so a production build can never honour one.
 *
 * Kept deliberately small: this module is imported by `stores/chat/repository.ts`
 * on the app's hot path. Everything the environment picker alone needs (listing,
 * joining, health) lives in `lib/sandboxEnvironments.ts`.
 */

export const CHATBOT_OVERRIDE_KEY = "CHATBOT_OVERRIDE";
export const SUPABASE_OVERRIDE_KEY = "SUPABASE_OVERRIDE";

/**
 * The only hosts we will hand a live Supabase access token to.
 *
 * Nearly every chatbot call sends the user's access token as a bearer token, so
 * an override URL is a credential-exfiltration primitive: whichever host it names
 * receives a working key to the user's database. Hence a closed set of our own
 * dev/sandbox hosts. Production (chat.franklin.ee) is deliberately absent — a
 * preview has no business talking to it.
 */
const ALLOWED_CHATBOT_HOSTNAMES = new Set([
  "chat-dev.franklin.ee",
  "chat-sandbox.franklin.ee",
  "localhost",
  "127.0.0.1",
]);

/**
 * The only hosts we will let become the app's Supabase project.
 *
 * The branch override is handed straight to `createClient`, so whichever host it names
 * receives the user's email and password at sign-in and every access token afterwards —
 * the same exposure as the chatbot override, and so the same treatment: a closed set,
 * checked on write AND on read, because localStorage is writable by anything running on
 * the origin. A project URL is always exactly `https://<ref>.supabase.co`.
 */
const SUPABASE_PROJECT_HOST = /^[a-z0-9-]+\.supabase\.co$/i;

/** Whether `url` is a Supabase project we are willing to sign the user in against. */
export function isSupabaseProjectUrl(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && SUPABASE_PROJECT_HOST.test(parsed.hostname);
  } catch {
    return false;
  }
}

/** What the Supabase branch override stores; see `integrations/supabase/client.ts`. */
export interface SupabaseBranchOverride {
  url: string;
  anonKey: string;
  branchName: string;
}

/** Dev-only switchers (and the overrides they write) are enabled per build. */
export function isChatbotOverrideEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_BRANCH_SWITCHER === "true";
}

function localStorageAvailable(): boolean {
  return typeof localStorage !== "undefined" && typeof localStorage.getItem === "function";
}

/**
 * Accept only a chatbot we are willing to authenticate against: an allowlisted
 * host over HTTPS, or plain HTTP on loopback for local development.
 *
 * @returns the normalised base URL without a trailing slash, or null if the
 * input is not a URL we will talk to.
 */
export function normalizeChatbotUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }

  if (!ALLOWED_CHATBOT_HOSTNAMES.has(parsed.hostname)) return null;

  const isLoopback = parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1";
  const isSafeScheme = parsed.protocol === "https:" || (parsed.protocol === "http:" && isLoopback);
  if (!isSafeScheme) return null;

  // Request paths (/chatbot/query/stream, …) are appended by the chat repository,
  // and a sandbox chatbot is addressed by a path on the router
  // (https://chat-sandbox.franklin.ee/<environment>), so keep the base path and
  // drop only the trailing slash.
  return `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, "");
}

/** The overridden chatbot base URL, or null when there is no usable override. */
export function readChatbotOverride(): string | null {
  if (!isChatbotOverrideEnabled() || !localStorageAvailable()) return null;
  const stored = localStorage.getItem(CHATBOT_OVERRIDE_KEY);
  // Re-validate on read — the stored value is user-writable, and the allowlist may
  // have tightened since it was written.
  return stored ? normalizeChatbotUrl(stored) : null;
}

/** The chatbot the app should talk to: the override if set, else the build-time value. */
export function resolveChatbotBaseUrl(): string | undefined {
  return readChatbotOverride() ?? import.meta.env.VITE_CHATBOT_URL;
}

/**
 * The active Supabase branch override, or null when the app is on the default project.
 *
 * The single reader of the key: `integrations/supabase/client.ts` and the TUS uploader
 * both come through here, so the flag, the JSON shape and the host allowlist are enforced
 * in one place and the app can never sign in against a project the uploader would refuse
 * (or the other way round).
 */
function parseSupabaseOverride(stored: string): SupabaseBranchOverride | null {
  const parsed = JSON.parse(stored) as Partial<SupabaseBranchOverride> | null;
  if (!parsed?.url || !parsed.anonKey) return null;
  if (!isSupabaseProjectUrl(parsed.url)) return null;
  return {
    url: parsed.url,
    anonKey: parsed.anonKey,
    branchName: parsed.branchName ?? parsed.url,
  };
}

export function readSupabaseOverride(): SupabaseBranchOverride | null {
  if (!isChatbotOverrideEnabled() || !localStorageAvailable()) return null;
  const stored = localStorage.getItem(SUPABASE_OVERRIDE_KEY);
  if (!stored) return null;
  try {
    return parseSupabaseOverride(stored);
  } catch (error) {
    console.warn("Failed to parse SUPABASE_OVERRIDE, using the build-time project:", error);
    return null;
  }
}

/**
 * The Supabase project the app is currently using — branch-override aware, so it
 * mirrors `integrations/supabase/client.ts`.
 */
export function getActiveSupabaseUrl(): string | undefined {
  return readSupabaseOverride()?.url ?? import.meta.env.VITE_SUPABASE_URL;
}

/** `oveburdswqlzzmdzlawo` from `https://oveburdswqlzzmdzlawo.supabase.co`. */
export function supabaseProjectRef(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const [ref] = new URL(url).hostname.split(".");
    return ref || null;
  } catch {
    return null;
  }
}
